import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Users, BookOpen, Award, Heart } from 'lucide-react';
import { schoolDivisions } from '../../data/schoolInfo';
import collegeImg from '../../assets/college.jpg';

const SchoolCards = () => {
  const schools = [
    {
      ...schoolDivisions.montessori,
      image: 'https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=600&h=400&fit=crop',
      color: 'emerald',
      icon: Heart,
    },
    {
      ...schoolDivisions.college,
      image: collegeImg,
      color: 'primary',
      icon: Award,
    },
  ];

  return (
    <section className="section-padding bg-gray-50">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8 md:mb-12"
        >
          <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">
            Our Schools
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-gray-900 mb-4">
            Two Divisions, One Vision
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            From early childhood through secondary education, we provide a seamless
            educational journey tailored to each stage of development.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {schools.map((school, index) => (
            <motion.div
              key={school.shortName}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
            >
              <Link
                to={school.href}
                className="group block bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-hard transition-all duration-300"
              >
                {/* Image */}
                <div className="relative h-48 md:h-64 overflow-hidden">
                  <img
                    src={school.image}
                    alt={school.shortName}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-6">
                    <span className="text-xs md:text-sm text-white/80">{school.ageRange}</span>
                    <h3 className="text-xl md:text-2xl font-bold text-white">{school.shortName}</h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 md:p-6">
                  <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">{school.description}</p>

                  {/* Features */}
                  <ul className="space-y-2 mb-4 md:mb-6 hidden md:block">
                    {school.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center text-primary-600 font-semibold group-hover:gap-3 transition-all text-sm md:text-base">
                    Explore {school.shortName}
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SchoolCards;
